// ─────────────────────────────────────────────────────────────
// "Offener Prozess" infoscreen template.
//
// Everything that can sensibly be configured is expressed as a
// {{placeholder}} that maps to a variable (see TEMPLATE_VARIABLES).
// Built-in tokens available at render time: {{ASSET_BASE}} (points at
// the backend /uploads/infoscreen folder), {{API_BASE}},
// {{INFOSCREEN_ID}}, {{BASE_W}}, {{BASE_H}}.
// ─────────────────────────────────────────────────────────────

export type VariableType = 'text' | 'textarea' | 'url' | 'image' | 'color' | 'number';

export interface TemplateVariable {
  key: string;
  label: string;
  type: VariableType;
  value: string;
}

export const OFFENER_PROZESS_HTML = `<div id="scale-wrapper">

    <!-- Background SVG (full-page) -->
    <img src="{{backgroundUrl}}" alt="" class="bg-layer">

    <!-- Logo -->
    <div class="brand">
        <img src="{{logoUrl}}" alt="{{logoAlt}}" class="brand-logo">
        <div class="brand-info"></div>
    </div>

    <!-- Öffnungszeiten -->
    <div class="section section--oeffnung">
        <h2 class="section-title section-title--banner section-title--oeffnung">{{titleOeffnungszeiten}}</h2>
        <div class="oeffnung-grid">
            <div class="oeffnung-card">
                <div class="oeffnung-card-title">{{labelAusstellung}} <div class="status-badge" id="status-anzeige">–</div></div>
                <div id="list-ausstellung"></div>
            </div>
            <div class="oeffnung-card">
                <div class="oeffnung-card-title">{{labelArchiv}} <div class="status-badge" id="status-archiv">–</div></div>
                <div id="list-archiv"></div>
            </div>
            <div class="oeffnung-card">
                <div class="oeffnung-card-title">{{labelAssembly}} <div class="status-badge" id="status-assembly">–</div></div>
                <div id="list-assembly"></div>
            </div>
            <div id="ofz-ticker-cards"></div>
        </div>
    </div>

    <!-- Veranstaltungen -->
    <div class="section section--events">
        <h2 class="section-title section-title--banner section-title--oeffnung">{{titleVeranstaltungen}}</h2>
        <div class="feed-list" id="feed-list-events"></div>
    </div>

    <!-- Führungen -->
    <div class="section section--tours">
        <h2 class="section-title section-title--banner section-title--oeffnung">{{titleFuehrungen}}</h2>
        <div class="feed-list" id="feed-list-tours"></div>
    </div>

    <!-- Allgemeine Informationen -->
    <div class="section section--info">
        <div class="info-card">
            <h2 class="info-card-title">{{titleInfo}}</h2>
            <div id="live-ticker"></div>
        </div>
    </div>

    <!-- Footer -->
    <div class="section section--footer">
        <img src="{{footerUrl}}" alt="" class="footer-image">
    </div>

</div>`;

export const OFFENER_PROZESS_CSS = `:root {
    --navy:    {{colorNavy}};
    --mid:     {{colorMid}};
    --blue:    {{colorBlue}};
    --lime:    {{colorLime}};
    --cream:   {{colorCream}};
    --white:   {{colorWhite}};
    --accent:  {{colorAccent}};
    --divider: rgba(199, 9, 9, 0.12);

    --pad:      {{sidePadding}}px;
    --card-gap:  16px;
}

*, *::before, *::after {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

html, body {
    width: 100vw;
    height: 100vh;
    overflow: hidden;
    background: #000;
    font-family: {{fontFamily}};
    color: var(--white);
}

#scale-wrapper {
    width: {{BASE_W}}px;
    height: {{BASE_H}}px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform-origin: center center;
    overflow: hidden;
    background: {{pageBackground}};
}

.bg-layer {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
}

.brand {
    position: absolute;
    top: 75px;
    right: var(--pad);
    left: auto;
    z-index: 5;
}
.brand-logo {
    width: {{logoWidth}}px;
    height: auto;
    display: block;
}
.brand-info {
    margin-top: 18px;
    font-size: 38px;
    font-weight: 500;
    color: var(--white);
    letter-spacing: 0.5px;
    text-align: left;
    white-space: nowrap;
}

.section {
    position: relative;
    z-index: 1;
    padding: 0 var(--pad);
}

.section-title {
    font-size: 118px;
    font-weight: 800;
    line-height: 1.0;
    margin-bottom: 28px;
    color: var(--white);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-left: 0;
    margin-right: 0;
    padding: 0;
}

.section-title--banner {
    background: var(--lime);
    color: var(--accent);
    display: inline-block;
    padding: 30px 40px 30px var(--pad);
    margin-left: calc(-1 * var(--pad));
    border-radius: 0;
    margin-bottom: 0;
    font-size: 90px;
    text-transform: none;
    letter-spacing: 0;
}

.section-title--info {
    color: var(--navy);
    font-size: 98px;
    text-transform: none;
    letter-spacing: 0;
}

.section--oeffnung {
    padding-top: 175px;
}

.section-title--oeffnung {
    display: block;
    width: calc(50% + var(--pad) - var(--card-gap) / 2);
    margin-bottom: 0;
}

.oeffnung-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 16px;
}

.oeffnung-card {
    position: relative;
    background: var(--navy);
    padding: 28px 37px 28px 37px;
    flex: 1 1 calc(50% - 8px);
    min-width: calc(50% - 8px);
    max-width: calc(50% - 8px);
    height: 400px;
    overflow: hidden;
}

#ofz-ticker-cards {
    display: contents;
}

.ofz-subtext {
    font-size: 14px;
    line-height: 1.4;
    color: var(--cream);
    margin-top: 20px;
    overflow: hidden;
}
.ofz-subtext p { margin-bottom: 10px; }
.ofz-subtext p:last-child { margin-bottom: 0; }

.oeffnung-card-title {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    position: relative;
    font-size: 70px;
    font-weight: 800;
    margin-bottom: 6px;
    color: var(--white);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status-badge {
    display: inline-block;
    font-size: 27px;
    font-weight: 600;
    padding: 5px 16px;
    border-radius: 0;
    margin-bottom: 16px;
    text-align: center;
    line-height: 1.2;
}

.agenda-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 49px;
    border-bottom: 1px solid var(--divider);
    color: var(--cream);
}
.agenda-row:last-child { border-bottom: none; }
.agenda-row.today { font-weight: 700; color: var(--lime); }
.agenda-row .time { font-weight: 500; }
.agenda-row .desc { text-align: right; font-variant-numeric: tabular-nums; }

#ofz-ticker-cards .agenda-row {
    font-size: 29px;
    padding: 4px 0;
}

.section--events { padding-top: 40px; }
.section--tours  { padding-top: 40px; }

.feed-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.feed-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--navy);
    border-radius: 0;
    padding: 44px 56px;
    gap: 40px;
    color: var(--white);
    font-size: 32px;
    line-height: 1.6;
    box-shadow: 0 4px 32px 0 rgba(30,39,88,0.10);
}

.feed-meta {
    font-size: 28px;
    font-weight: 600;
    opacity: 0.8;
    color: var(--cream);
}
.feed-title {
    font-size: 50px;
    font-weight: 800;
    line-height: 1.18;
    color: var(--white);
    letter-spacing: 0.5px;
}
.feed-sub {
    font-size: 28px;
    opacity: 0.8;
    color: var(--cream);
    font-weight: 400;
}
.feed-left {
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 0;
    height: 140px;
    overflow: hidden;
}
.qr {
    width: 150px;
    height: 150px;
    margin-left: 12px;
    transform: scale(1.2);
    transform-origin: center center;
}

.section--info {
    padding-top: 50px;
    padding-bottom: 30px;
}

.info-card {
    background: var(--white);
    padding: 40px 40px;
}

.info-card-title {
    font-size: 64px;
    font-weight: 800;
    color: var(--navy);
    margin-bottom: 28px;
}

#live-ticker { min-height: 0; }

.ticker-item {
    background: var(--navy);
    border-radius: 0;
    padding: 24px 32px;
    margin-bottom: 12px;
    border-left: 5px solid var(--navy);
}
.ticker-item:last-child { margin-bottom: 0; }
.ticker-item.warning { border-left-color: #e6a000; background: var(--navy); }
.ticker-item.alert   { border-left-color: #d92d20; background: var(--navy); }
.ticker-item.info    { background: var(--white); border-left-color: var(--white); }

.ticker-text    { font-size: 30px; font-weight: 600; line-height: 1.3; color: var(--white); }
.ticker-subtext { font-size: 34px; font-weight: 400; margin-top: 6px; opacity: 0.7; color: var(--cream); }

.ticker-item.info .ticker-text    { color: var(--navy); }
.ticker-item.info .ticker-subtext { color: var(--navy); opacity: 0.7; }
.no-ticker { display: none; }

.section--footer {
    position: absolute;
    bottom: 20px;
    left: 0;
    right: 0;
    text-align: center;
    background: transparent;
}

.footer-image {
    display: block;
    margin: 0 auto;
    width: {{footerWidth}}px;
    height: auto;
    margin-left: calc(50% - {{footerHalfWidth}}px + 100px);
}`;

export const OFFENER_PROZESS_JS = `(function() {
    if (window.__OP_KIOSK_INIT__) return;
    window.__OP_KIOSK_INIT__ = true;

    var OEFFNUNGSZEITEN_URL = '{{oeffnungszeitenUrl}}';
    var FEED_URL = '{{feedUrl}}';
    var TICKER_URL = '{{tickerUrl}}';
    var WOCHENTAGE = ["Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag","Sonntag"];
    var EVENT_ITEMS_COUNT = {{eventItemsCount}};
    var TOUR_ITEMS_COUNT = {{tourItemsCount}};
    var TOUR_PATTERN = new RegExp('{{tourPattern}}', 'i');
    var QR_ENDPOINT = '{{qrEndpoint}}';
    var BASE_W = {{BASE_W}};
    var BASE_H = {{BASE_H}};

    function scaleToFit() {
        var wrapper = document.getElementById('scale-wrapper');
        if (!wrapper) return;
        var vw = window.innerWidth;
        var vh = window.innerHeight;
        var scale = Math.min(vw / BASE_W, vh / BASE_H);
        wrapper.style.transform = 'translate(-50%, -50%) scale(' + scale + ')';
    }
    window.addEventListener('resize', scaleToFit);
    window.addEventListener('load', scaleToFit);
    document.addEventListener('DOMContentLoaded', scaleToFit);
    requestAnimationFrame(scaleToFit);
    setTimeout(scaleToFit, 100);

    function padToExact(items, n) {
        var out = items.slice(0, n);
        while (out.length < n) {
            out.push({ title: "Weitere Termine folgen", pubDate: new Date().toISOString(), timeframe: "", location: "", ort: "", link: "" });
        }
        return out;
    }

    var TAG_KURZ = { "Montag":"Mo","Dienstag":"Di","Mittwoch":"Mi","Donnerstag":"Do","Freitag":"Fr","Samstag":"Sa","Sonntag":"So" };

    function formatZeit(zeit) {
        if (!zeit || zeit === "geschlossen") return "geschlossen";
        return zeit.replace(/:00/g, '').replace('-', '–') + ' Uhr';
    }

    function formatTageGruppe(tage) {
        if (tage.length === 1) return TAG_KURZ[tage[0]];
        if (tage.length === 2) return TAG_KURZ[tage[0]] + ', ' + TAG_KURZ[tage[1]];
        var indices = tage.map(function(t) { return WOCHENTAGE.indexOf(t); });
        var isConsecutive = true;
        for (var i = 1; i < indices.length; i++) {
            if (indices[i] !== indices[i-1] + 1) { isConsecutive = false; break; }
        }
        if (isConsecutive) return TAG_KURZ[tage[0]] + '–' + TAG_KURZ[tage[tage.length - 1]];
        return tage.map(function(t) { return TAG_KURZ[t]; }).join(', ');
    }

    function renderOeffnungszeitenList(data, listId) {
        var list = document.getElementById(listId);
        if (!list || !data) return;
        list.innerHTML = "";
        var heute = new Date().toLocaleDateString('de-DE', { weekday: 'long' });
        var zeitMap = {};
        var zeitOrder = [];
        WOCHENTAGE.forEach(function(tag) {
            var eintrag = data[tag];
            if (eintrag === undefined) return;
            var zeit = eintrag || "geschlossen";
            if (!zeitMap[zeit]) { zeitMap[zeit] = []; zeitOrder.push(zeit); }
            zeitMap[zeit].push(tag);
        });
        zeitOrder.forEach(function(zeit) {
            var tage = zeitMap[zeit];
            var row = document.createElement('div');
            row.className = "agenda-row";
            if (tage.indexOf(heute) !== -1) row.classList.add('today');
            var label = formatTageGruppe(tage);
            row.innerHTML = '<div class="time">' + label + '</div><div class="desc">' + formatZeit(zeit) + '</div>';
            list.appendChild(row);
        });
    }

    function checkObGeoeffnetSingle(zeiten, statusId) {
        var statusEl = document.getElementById(statusId);
        if (!statusEl || !zeiten) return;
        var now = new Date();
        var wochentag = now.toLocaleDateString('de-DE', { weekday: 'long' });
        var eintrag = zeiten[wochentag];
        if (!eintrag || eintrag === "geschlossen") {
            statusEl.innerHTML = "Heute<br>geschlossen";
            statusEl.style.color = "#d92d20";
            statusEl.style.background = "rgba(217,45,32,0.12)";
            return;
        }
        var parts = eintrag.split('-');
        var von = parts[0], bis = parts[1];
        if (!von || !bis) { statusEl.textContent = eintrag; return; }
        var vonParts = von.split(':'), bisParts = bis.split(':');
        var vonDate = new Date(now); vonDate.setHours(parseInt(vonParts[0],10), parseInt(vonParts[1]||'0',10), 0, 0);
        var bisDate = new Date(now); bisDate.setHours(parseInt(bisParts[0],10), parseInt(bisParts[1]||'0',10), 0, 0);
        if (now >= vonDate && now <= bisDate) {
            statusEl.innerHTML = "Jetzt<br>geöffnet";
            statusEl.style.color = "#1f8a4c";
            statusEl.style.background = "rgba(31,138,76,0.14)";
        } else {
            statusEl.innerHTML = "Derzeit<br>geschlossen";
            statusEl.style.color = "#d92d20";
            statusEl.style.background = "rgba(217,45,32,0.12)";
        }
    }

    function ladeAlleOeffnungszeiten() {
        if (!OEFFNUNGSZEITEN_URL) return;
        fetch(OEFFNUNGSZEITEN_URL, { cache: "no-store" })
            .then(function(r) { return r.json(); })
            .then(function(z) {
                renderOeffnungszeitenList(z.ausstellung, "list-ausstellung");
                renderOeffnungszeitenList(z.archiv, "list-archiv");
                renderOeffnungszeitenList(z.assembly, "list-assembly");
                checkObGeoeffnetSingle(z.ausstellung, "status-anzeige");
                checkObGeoeffnetSingle(z.archiv, "status-archiv");
                checkObGeoeffnetSingle(z.assembly, "status-assembly");
            }).catch(function(e) { console.error('Öffnungszeiten', e); });
    }
    ladeAlleOeffnungszeiten();
    setInterval(ladeAlleOeffnungszeiten, 60000);

    function fitTextToContainer(element) {
        if (!element) return;
        var parent = element.parentElement;
        if (!parent) return;
        var title = parent.querySelector('.oeffnung-card-title');
        var titleHeight = title ? title.offsetHeight : 0;
        var parentStyle = getComputedStyle(parent);
        var paddingTop = parseFloat(parentStyle.paddingTop) || 0;
        var paddingBottom = parseFloat(parentStyle.paddingBottom) || 0;
        var availableHeight = parent.offsetHeight - paddingTop - paddingBottom - titleHeight - 20;
        var fontSize = 28;
        var minFontSize = 14;
        element.style.fontSize = fontSize + 'px';
        while (element.scrollHeight > availableHeight && fontSize > minFontSize) {
            fontSize -= 1;
            element.style.fontSize = fontSize + 'px';
        }
    }

    function isMessageActive(m) {
        var now = new Date();
        return new Date(m.startTime) <= now && now <= new Date(m.endTime);
    }

    function renderTicker(data) {
        var ticker = document.getElementById('live-ticker');
        var ofzContainer = document.getElementById('ofz-ticker-cards');
        if (!ticker) return;
        ticker.innerHTML = '';
        if (ofzContainer) ofzContainer.innerHTML = '';
        var active = (data.messages || []).filter(isMessageActive);
        var ofzMessages = active.filter(function(m) { return m.type === 'ofz'; });
        var ofzTypeMessages = active.filter(function(m) { return m.type === 'ofztype'; });
        var otherMessages = active.filter(function(m) { return m.type !== 'ofz' && m.type !== 'ofztype'; });
        if (ofzContainer) {
            ofzMessages.forEach(function(msg) {
                var card = document.createElement('div');
                card.className = 'oeffnung-card';
                card.innerHTML = '<div class="oeffnung-card-title">' + msg.text + '</div>' +
                    (msg.subtext ? '<div class="ofz-subtext">' + msg.subtext.split(/\\r?\\n\\r?\\n/).map(function(p) { return '<p>' + p.replace(/\\r?\\n/g, '<br>') + '</p>'; }).join('') + '</div>' : '');
                ofzContainer.appendChild(card);
            });
            ofzTypeMessages.forEach(function(msg) {
                var card = document.createElement('div');
                card.className = 'oeffnung-card';
                var lines = (msg.subtext || '').split(/\\r?\\n/).filter(function(l) { return l.trim() !== ''; });
                var rowsHtml = lines.map(function(line) {
                    return '<div class="agenda-row"><div class="time">' + line + '</div></div>';
                }).join('');
                card.innerHTML = '<div class="oeffnung-card-title">' + msg.text + '</div>' + rowsHtml;
                ofzContainer.appendChild(card);
            });
            setTimeout(function() {
                var subtexts = ofzContainer.querySelectorAll('.ofz-subtext');
                subtexts.forEach(fitTextToContainer);
            }, 0);
        }
        if (otherMessages.length === 0) {
            ticker.innerHTML = '<div class="no-ticker">Keine aktuellen Meldungen</div>';
            return;
        }
        otherMessages.forEach(function(msg) {
            var el = document.createElement('div');
            el.className = 'ticker-item ' + (msg.type || 'info');
            el.innerHTML = '<div class="ticker-text">' + msg.text + '</div>' +
                (msg.subtext ? '<div class="ticker-subtext">' + msg.subtext + '</div>' : '');
            ticker.appendChild(el);
        });
    }

    function fetchTicker() {
        if (!TICKER_URL) return;
        fetch(TICKER_URL, { cache: "no-store" })
            .then(function(r) { return r.json(); })
            .then(function(d) { renderTicker(d); })
            .catch(function() {
                renderTicker({ messages: [] });
            });
    }
    fetchTicker();
    setInterval(fetchTicker, 300000);

    function parseDescription(desc) {
        if (!desc) return {};
        var clean = desc.replace('<![CDATA[','').replace(']]>','');
        var p = clean.split(/<br\\s*\\/?>/i);
        return { timeframe: (p[0]||'').trim(), location: (p[1]||'').trim(), ort: (p[2]||'').trim(), stadt: (p[3]||'').trim() };
    }

    function isEventInFuture(item) {
        var now = new Date(); now.setHours(0,0,0,0);
        var d = new Date(item.pubDate);
        return !isNaN(d.getTime()) && d >= now;
    }

    function isTourItem(item) { return TOUR_PATTERN.test(item.title); }

    function removeQuotedText(text) {
        if (!text) return text;
        return text.replace(/\\s*[„"].*?[""]/g, '').trim();
    }

    function truncateTitle(text, maxLen) {
        if (!text || text.length <= maxLen) return text;
        var truncated = text.substring(0, maxLen);
        var lastSpace = truncated.lastIndexOf(' ');
        if (lastSpace > 0) truncated = truncated.substring(0, lastSpace);
        return truncated.trim() + '…';
    }

    function renderList(items, containerId) {
        var list = document.getElementById(containerId);
        if (!list) return;
        list.innerHTML = '';
        items.forEach(function(item) {
            var d = new Date(item.pubDate);
            var dateLabel = item.timeframe || (
                (isNaN(d.getTime()) ? "" : d.toLocaleDateString('de-DE', { weekday:'short', day:'2-digit', month:'long' })) +
                (isNaN(d.getTime()) ? "" : " · " + d.toLocaleTimeString('de-DE', { hour:'2-digit', minute:'2-digit' }))
            );
            var el = document.createElement('div');
            el.className = 'feed-item';
            el.innerHTML =
                '<div class="feed-left">' +
                    '<div class="feed-meta">' + (dateLabel||' ') + '</div>' +
                    '<div class="feed-title">' + truncateTitle(removeQuotedText(item.title||''), 40) + '</div>' +
                    '<div class="feed-sub">' + (item.ort||' ') + '</div>' +
                '</div>' +
                '<div class="qr-wrap"><img class="qr" alt="QR" src="' + QR_ENDPOINT +
                    encodeURIComponent(item.link||'') + '"></div>';
            list.appendChild(el);
        });
    }

    function fetchFeed() {
        if (!FEED_URL) return;
        fetch(FEED_URL, { cache: "no-store" })
            .then(function(r) { return r.text(); })
            .then(function(text) {
                var xml = new DOMParser().parseFromString(text, "application/xml");
                var nodes = xml.querySelectorAll('item');
                var items = [];
                for (var i = 0; i < nodes.length; i++) {
                    var n = nodes[i];
                    var desc = n.querySelector('description');
                    var parsed = parseDescription(desc ? desc.textContent.trim() : '');
                    var t = n.querySelector('title'), p = n.querySelector('pubDate'), l = n.querySelector('link');
                    items.push({
                        title: t ? t.textContent.trim() : '',
                        timeframe: parsed.timeframe, location: parsed.location,
                        ort: parsed.ort, stadt: parsed.stadt,
                        pubDate: p ? p.textContent.trim() : '',
                        link: l ? l.textContent.trim() : ''
                    });
                }
                items = items.filter(isEventInFuture);
                var events = items.filter(function(i) { return !isTourItem(i); });
                var tours  = items.filter(isTourItem);
                renderList(events.slice(0, EVENT_ITEMS_COUNT), 'feed-list-events');
                renderList(padToExact(tours, TOUR_ITEMS_COUNT), 'feed-list-tours');
            })
            .catch(function() {
                renderList([], 'feed-list-events');
                renderList(padToExact([], TOUR_ITEMS_COUNT), 'feed-list-tours');
            });
    }
    fetchFeed();
    setInterval(fetchFeed, 300000);
})();`;

export const OFFENER_PROZESS_VARIABLES: TemplateVariable[] = [
  { key: 'logoUrl', label: 'Logo', type: 'image', value: '{{ASSET_BASE}}/logo.svg' },
  { key: 'logoAlt', label: 'Logo Alt-Text', type: 'text', value: 'offener prozess' },
  { key: 'logoWidth', label: 'Logo-Breite (px)', type: 'number', value: '460' },
  { key: 'backgroundUrl', label: 'Hintergrundbild', type: 'image', value: '{{ASSET_BASE}}/bg.svg' },
  { key: 'footerUrl', label: 'Footer-Bild', type: 'image', value: '{{ASSET_BASE}}/footer.png' },
  { key: 'footerWidth', label: 'Footer-Breite (px)', type: 'number', value: '1000' },
  { key: 'footerHalfWidth', label: 'Footer halbe Breite (px)', type: 'number', value: '500' },
  { key: 'feedUrl', label: 'Feed-URL (gedumpt, CMS)', type: 'url', value: '{{API_BASE}}/infoscreens/{{INFOSCREEN_ID}}/feed' },
  { key: 'oeffnungszeitenUrl', label: 'Öffnungszeiten (CMS)', type: 'url', value: '{{API_BASE}}/infoscreens/{{INFOSCREEN_ID}}/oeffnungszeiten.json' },
  { key: 'tickerUrl', label: 'Ticker (CMS)', type: 'url', value: '{{API_BASE}}/infoscreens/{{INFOSCREEN_ID}}/ticker.json' },
  { key: 'titleOeffnungszeiten', label: 'Titel: Öffnungszeiten', type: 'text', value: 'Öffnungszeiten' },
  { key: 'titleVeranstaltungen', label: 'Titel: Veranstaltungen', type: 'text', value: 'Veranstaltungen' },
  { key: 'titleFuehrungen', label: 'Titel: Führungen', type: 'text', value: 'Führungen' },
  { key: 'titleInfo', label: 'Titel: Allgemeine Informationen', type: 'text', value: 'Allgemeine Informationen' },
  { key: 'labelAusstellung', label: 'Karte: Ausstellung', type: 'text', value: 'Ausstellung' },
  { key: 'labelArchiv', label: 'Karte: Archiv', type: 'text', value: 'Archiv' },
  { key: 'labelAssembly', label: 'Karte: Assembly', type: 'text', value: 'Assembly' },
  { key: 'eventItemsCount', label: 'Anzahl Veranstaltungen', type: 'number', value: '3' },
  { key: 'tourItemsCount', label: 'Anzahl Führungen', type: 'number', value: '2' },
  { key: 'tourPattern', label: 'Führungs-Erkennung (Regex)', type: 'text', value: 'Critical Walk|Führung' },
  { key: 'qrEndpoint', label: 'QR-Code Endpoint', type: 'url', value: 'https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=' },
  { key: 'colorNavy', label: 'Farbe: Navy (Karten)', type: 'color', value: '#1e2758' },
  { key: 'colorMid', label: 'Farbe: Mid', type: 'color', value: '#3d487e' },
  { key: 'colorBlue', label: 'Farbe: Blau', type: 'color', value: '#5e7db4' },
  { key: 'colorLime', label: 'Farbe: Lime (Banner)', type: 'color', value: '#edf489' },
  { key: 'colorCream', label: 'Farbe: Cream (Text)', type: 'color', value: '#fdf8f0' },
  { key: 'colorWhite', label: 'Farbe: Weiß', type: 'color', value: '#ffffff' },
  { key: 'colorAccent', label: 'Farbe: Akzent (Banner-Text)', type: 'color', value: '#ed6547' },
  { key: 'pageBackground', label: 'Seiten-Hintergrund', type: 'text', value: '#ffffff' },
  { key: 'sidePadding', label: 'Seiten-Padding (px)', type: 'number', value: '120' },
  { key: 'fontFamily', label: 'Schriftart', type: 'text', value: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif" },
];

export const OFFENER_PROZESS_TEMPLATE = {
  name: 'Offener Prozess',
  baseWidth: 2160,
  baseHeight: 3840,
  customHtml: OFFENER_PROZESS_HTML,
  customCss: OFFENER_PROZESS_CSS,
  customJs: OFFENER_PROZESS_JS,
  variables: OFFENER_PROZESS_VARIABLES,
};
